import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Send,
  Flag,
  Check,
  Person,
  Schedule,
  Delete,
  Warning,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

/**
 * CommentThread Component
 *
 * Shows comments for a specific segment with ability to add new comments
 * and mark segment for review.
 */
const CommentThread = ({ transcriptId, segmentIndex, comments, onCommentAdded, onCommentDeleted }) => {
  const [newComment, setNewComment] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [markAsReview, setMarkAsReview] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      return;
    }

    try {
      setAdding(true);
      setError(null);

      if (markAsReview) {
        // Use the review endpoint
        await apiService.markSegmentForReview(
          transcriptId,
          segmentIndex,
          newComment,
          authorName || null
        );
      } else {
        // Regular comment
        await apiService.createComment(transcriptId, {
          segment_index: segmentIndex,
          comment_text: newComment,
          author_name: authorName || null,
          comment_type: 'comment',
          needs_review: false,
        });
      }

      setNewComment('');
      setMarkAsReview(false);

      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (err) {
      setError('Failed to add comment: ' + err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) {
      return;
    }

    try {
      await apiService.deleteComment(commentId);
      if (onCommentDeleted) {
        onCommentDeleted();
      }
    } catch (err) {
      setError('Failed to delete comment: ' + err.message);
    }
  };

  const handleResolveComment = async (comment) => {
    try {
      await apiService.updateComment(comment.id, {
        is_resolved: !comment.is_resolved,
      });
      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (err) {
      setError('Failed to update comment: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const hasUnresolvedReviews = comments?.some(c => c.needs_review && !c.is_resolved);

  return (
    <Paper elevation={1} sx={{ p: 2, mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle2">
          Comments on Segment {segmentIndex + 1}
        </Typography>
        {comments && comments.length > 0 && (
          <Chip label={comments.length} size="small" />
        )}
        {hasUnresolvedReviews && (
          <Chip
            label="Needs Review"
            size="small"
            color="warning"
            icon={<Warning />}
          />
        )}
      </Box>

      {/* Existing Comments */}
      {comments && comments.length > 0 && (
        <Box sx={{ mb: 2, maxHeight: 300, overflow: 'auto' }}>
          {comments.map((comment) => (
            <Paper
              key={comment.id}
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 1,
                bgcolor: comment.is_resolved ? 'action.disabledBackground' : 'background.paper',
                borderLeft: comment.needs_review ? '4px solid' : 'none',
                borderLeftColor: 'warning.main',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {comment.author_name && (
                    <Chip
                      label={comment.author_name}
                      size="small"
                      icon={<Person />}
                      sx={{ height: 20 }}
                    />
                  )}
                  {comment.needs_review && (
                    <Chip
                      label="Review"
                      size="small"
                      color="warning"
                      icon={<Flag />}
                      sx={{ height: 20 }}
                    />
                  )}
                  {comment.is_resolved && (
                    <Chip
                      label="Resolved"
                      size="small"
                      color="success"
                      icon={<Check />}
                      sx={{ height: 20 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {comment.needs_review && (
                    <Tooltip title={comment.is_resolved ? 'Mark as unresolved' : 'Mark as resolved'}>
                      <IconButton
                        size="small"
                        onClick={() => handleResolveComment(comment)}
                      >
                        <Check
                          fontSize="small"
                          color={comment.is_resolved ? 'success' : 'disabled'}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteComment(comment.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {comment.comment_text}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Schedule sx={{ fontSize: 12, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {formatDate(comment.created_at)}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Add Comment Form */}
      <Box>
        <TextField
          fullWidth
          size="small"
          placeholder="Add a comment or mark for review..."
          multiline
          rows={2}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          disabled={adding}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              placeholder="Your name (optional)"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              disabled={adding}
              sx={{ width: 150 }}
            />
            <Button
              size="small"
              variant={markAsReview ? 'contained' : 'outlined'}
              color="warning"
              startIcon={<Flag />}
              onClick={() => setMarkAsReview(!markAsReview)}
              disabled={adding}
            >
              {markAsReview ? 'Review Mode' : 'Mark for Review'}
            </Button>
          </Box>
          <Button
            size="small"
            variant="contained"
            startIcon={adding ? <CircularProgress size={16} /> : <Send />}
            onClick={handleAddComment}
            disabled={adding || !newComment.trim()}
          >
            {markAsReview ? 'Flag for Review' : 'Add Comment'}
          </Button>
        </Box>

        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default CommentThread;
